/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IMsdyn365CsChatConnectorViewProps, IChatData } from '../index';

/**
 * Returns data parameters for Omnichannel chat script.
 * @param {IMsdyn365CsChatConnectorViewProps} props The properties.
 * @returns {IChatData} Chat data.
 */
const getChatData = (props: IMsdyn365CsChatConnectorViewProps): IChatData => {
    const data: IChatData = {
        'data-app-id': props.config.dataAppId || props.context.app.config.msDyn365CsChatConnectorDataAppId,
        'data-org-id': props.config.dataOrgId || props.context.app.config.msDyn365CsChatConnectorDataOrgId,
        'data-org-url': props.config.dataOrgUrl || props.context.app.config.msDyn365CsChatConnectorDataOrgUrl
    };
    if (props.config.dataHideChatButton !== undefined) {
        data['data-hide-chat-button'] = props.config.dataHideChatButton;
    } else if (props.context.app.config.msDyn365CsChatConnectorDataHideChatButton) {
        data['data-hide-chat-button'] = props.context.app.config.msDyn365CsChatConnectorDataHideChatButton;
    }
    if (props.config.dataColorOverride) {
        data['data-color-override'] = props.config.dataColorOverride;
    } else if (props.context.app.config.msDyn365CsChatConnectorDataColorOverride) {
        data['data-color-override'] = props.context.app.config.msDyn365CsChatConnectorDataColorOverride;
    }
    if (props.config.dataFontFamilyOverride) {
        data['data-font-family-override'] = props.config.dataFontFamilyOverride;
    } else if (props.context.app.config.msDyn365CsChatConnectorDataFontFamilyOverride) {
        data['data-font-family-override'] = props.context.app.config.msDyn365CsChatConnectorDataFontFamilyOverride;
    }
    if (props.config.dataEnableTelemetry) {
        data['data-disable-telemetry'] = !props.config.dataEnableTelemetry;
    }
    if (props.config.dataOpenInWindow) {
        data['data-open-in-window'] = props.config.dataOpenInWindow;
    }
    if (props.config.dataEnableLcwAutofill) {
        data['data-enable-lcw-autofill'] = props.config.dataEnableLcwAutofill;
    }
    if (props.config.dataSuggestedActionLayout !== undefined) {
        data['data-suggested-action-layout'] = props.config.dataSuggestedActionLayout;
    }
    return data;
};

export default (props: IMsdyn365CsChatConnectorViewProps) => {
    const data: IChatData = getChatData(props);

    // we cannot use server rendering here, because OCCS fires 'lcw:ready' event ASAP when initialized
    // and by the time when execution comes to Msdyn365CsChatConnector, that event is already fired
    // thus, we can only add OCCS script to DOM on client side to control execution flow.
    React.useEffect(() => {
        const script = document?.createElement('script');
        script.type = 'text/javascript';
        script.src = props.config.src || props.context.app.config.msDyn365CsChatConnectorSrc;
        script.id = 'Microsoft_Omnichannel_LCWidget';
        Object.keys(data).forEach(k => {
            script.setAttribute(k, data[k]);
        });
        document?.body?.appendChild(script);
    }, []);

    return null;
};
